/**
 * Formatea YYYY-MM-DD en zona local (sin UTC shift).
 */
export function formatLocalIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Genera N días hábiles a partir de mañana (excluye hoy y fines de semana).
 * TODO: Replace frontend-generated slots with backend/provider
 * availability if Backend exposes slot calendar later.
 */
export function buildAvailableVisitDates(fromDate = new Date(), count = 5) {
  const dates = [];
  const cursor = new Date(fromDate);
  cursor.setHours(0, 0, 0, 0);
  // Empieza mañana: no incluir hoy ni pasado.
  cursor.setDate(cursor.getDate() + 1);

  while (dates.length < count) {
    const weekday = cursor.getDay();
    if (weekday !== 0 && weekday !== 6) {
      dates.push(formatLocalIsoDate(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function formatClock(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Slots de visita cada 30 minutos (etiqueta intervalo).
 * Por defecto 09:00–17:00 → "09:00 - 09:30" … "16:30 - 17:00".
 */
export function buildAvailableVisitTimeSlots({
  startMinutes = 9 * 60,
  endMinutes = 17 * 60,
  stepMinutes = 30,
} = {}) {
  const slots = [];
  for (let start = startMinutes; start < endMinutes; start += stepMinutes) {
    const end = start + stepMinutes;
    if (end > endMinutes) break;
    slots.push(`${formatClock(start)} - ${formatClock(end)}`);
  }
  return slots;
}

export const DEFAULT_VISIT_TIME_SLOTS = Object.freeze(
  buildAvailableVisitTimeSlots(),
);

/**
 * Extrae HH:mm del inicio de un slot "09:00 - 09:30".
 */
export function getSlotStartTime(slot) {
  const start = String(slot || '').split(' - ')[0]?.trim();
  if (!/^\d{2}:\d{2}$/.test(start)) return null;
  return start;
}

/**
 * Combina fecha local YYYY-MM-DD y slot en date-time naive (hora México).
 * TODO: Replace frontend-generated slots with backend/provider
 * availability if Backend exposes slot calendar later.
 */
export function buildScheduledVisitAt(dateIso, timeSlot) {
  const start = getSlotStartTime(timeSlot);
  if (!dateIso || !start) return null;
  return `${dateIso}T${start}:00`;
}

/**
 * Formatea YYYY-MM-DD o date-time a DD/MM/YYYY.
 */
export function formatVisitDateDisplay(value) {
  if (!value) return '- -';
  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return '- -';
  return `${day}/${month}/${year}`;
}

/**
 * Extrae HH:mm de date-time ISO, con Z, o con espacio.
 */
export function extractVisitTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const isoMatch = raw.match(/T(\d{2}:\d{2})/);
  if (isoMatch) return isoMatch[1];
  const spaceMatch = raw.match(/\s(\d{2}:\d{2})/);
  if (spaceMatch) return spaceMatch[1];
  return null;
}

/**
 * True si el valor trae hora usable (no solo fecha).
 */
export function hasUsableVisitTime(value) {
  return !!extractVisitTime(value);
}

/**
 * Prefiere el date-time con hora. Si backend es date-only,
 * conserva el local/snapshot si es el mismo día.
 */
export function resolveScheduledVisitAt(preferred, fallback) {
  if (hasUsableVisitTime(preferred)) return preferred;
  if (hasUsableVisitTime(fallback)) {
    const preferredDate = String(preferred || '').slice(0, 10);
    const fallbackDate = String(fallback || '').slice(0, 10);
    if (!preferredDate || preferredDate === fallbackDate) {
      return fallback;
    }
  }
  return preferred || fallback || null;
}

/**
 * Formatea hora de visita desde date-time o slot.
 */
export function formatVisitTimeDisplay(scheduledVisitAt, timeSlot) {
  const start = getSlotStartTime(timeSlot);
  if (start) return start;
  return extractVisitTime(scheduledVisitAt) || '- -';
}

/**
 * True si la visita está a `hours` o menos (o ya pasó).
 */
export function isVisitWithinHours(scheduledVisitAt, hours = 24) {
  if (!scheduledVisitAt) return false;
  const parsed = Date.parse(String(scheduledVisitAt));
  if (Number.isNaN(parsed)) return false;
  return parsed - Date.now() <= hours * 60 * 60 * 1000;
}
